// v3/layouts/AppLayout.tsx — Main app layout
// Professional design with navy sidebar, clean header, no overflow issues.
// Zero imports from v1/v2.

import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../design-system/utils';
import { NAVIGATION_ITEMS } from '../config/navigation.config';
import { usePermission } from '../shared/hooks/usePermission';
import { useAuth } from '../shared/hooks/useAuth';
import { useIsMobile } from '../shared/hooks/useIsMobile';
import { useUIStore } from '../shared/stores/ui-store';
import { useAuthStore } from '../shared/stores/auth-store';
import { Breadcrumbs } from '../shared/components/Breadcrumbs';
import { LocationGuard } from '../shared/components/LocationGuard';
import { RumPageTracker } from '../shared/components/RumPageTracker';
import { MobileNav } from './MobileLayout';

function getIcon(name: string): LucideIcon {
  return (Icons as unknown as Record<string, LucideIcon>)[name] ?? Icons.Circle;
}

// ─── Sidebar ─────────────────────────────────────────────────────────────────

function Sidebar() {
  const { canAccessFeature } = usePermission();
  const sidebarOpen = useUIStore((s) => s.sidebarOpen);
  const items = NAVIGATION_ITEMS.filter((i) => canAccessFeature(i.featureId));

  return (
    <aside
      className={cn(
        'hidden md:flex flex-col shrink-0 transition-all duration-200',
        sidebarOpen ? 'w-52' : 'w-14',
      )}
      style={{ background: '#0f172a' }}
    >
      {/* Logo */}
      <div className={cn('flex items-center h-14 px-3 border-b border-white/10', !sidebarOpen && 'justify-center')}>
        <NavLink to="/">
          {sidebarOpen ? (
            <img src="/assets/logo2.jpg" alt="Pargro" className="h-9 rounded-lg" />
          ) : (
            <img src="/assets/icon.png" alt="Pargro" className="h-8 w-8 rounded" />
          )}
        </NavLink>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-1.5 px-1.5">
        {items.map((item) => {
          const Icon = getIcon(item.icon);
          return (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2 rounded-md px-2.5 py-2 text-[13px] font-medium transition-colors mb-0.5',
                  isActive ? 'bg-white/15 text-white' : 'text-white/60 hover:bg-white/8 hover:text-white/90',
                  !sidebarOpen && 'justify-center px-0',
                )
              }
            >
              <Icon className="h-4 w-4 shrink-0" />
              {sidebarOpen && <span className="truncate">{item.label}</span>}
            </NavLink>
          );
        })}
      </nav>
    </aside>
  );
}

// ─── Warning Badge ───────────────────────────────────────────────────────────

function WarningBadge() {
  const warnings = useAuthStore((s) => s.warnings);
  const [open, setOpen] = useState(false);

  if (warnings.length === 0) return null;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="relative flex h-8 w-8 items-center justify-center rounded-full hover:bg-amber-50 transition-colors"
        title={`${warnings.length} warning${warnings.length > 1 ? 's' : ''}`}
      >
        <Icons.AlertTriangle className="h-4.5 w-4.5 text-amber-500" />
        <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 text-[9px] font-bold text-white">
          {warnings.length}
        </span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-10 z-50 w-72 rounded-lg border bg-white shadow-lg overflow-hidden">
            <div className="px-3 py-2 border-b bg-amber-50">
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Account Warnings</p>
            </div>
            <div className="max-h-60 overflow-y-auto">
              {warnings.map((w) => (
                <div key={w.id} className="flex gap-2 px-3 py-2.5 border-b last:border-b-0">
                  <Icons.AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-500" />
                  <p className="text-sm text-foreground">{w.message}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Avatar Menu ─────────────────────────────────────────────────────────────

function AvatarMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  if (!user) return null;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex h-8 w-8 items-center justify-center rounded-full text-white text-xs font-bold"
        style={{ background: '#1e3a5f' }}
      >
        {(user.userName?.[0] || 'U').toUpperCase()}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-10 z-50 w-48 rounded-md border bg-white shadow-lg py-1 text-sm">
            <div className="px-3 py-2 border-b">
              <p className="font-medium text-foreground">{user.userName}</p>
              <p className="text-xs text-muted-foreground">
                {user.roleAssigned === false ? (
                  <span className="text-amber-600">No role assigned</span>
                ) : (
                  user.role?.replace(/_/g, ' ')
                )}
              </p>
            </div>
            <button onClick={() => { setOpen(false); navigate('/settings'); }} className="flex w-full items-center gap-2 px-3 py-2 hover:bg-slate-50 text-foreground">
              <Icons.Settings className="h-3.5 w-3.5" /> Settings
            </button>
            <button onClick={() => { setOpen(false); logout(); }} className="flex w-full items-center gap-2 px-3 py-2 hover:bg-slate-50 text-red-600">
              <Icons.LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Header ──────────────────────────────────────────────────────────────────

function Header() {
  const isMobile = useIsMobile();
  const toggle = useUIStore((s) => s.toggleSidebar);

  return (
    <header className="flex items-center h-12 px-3 border-b bg-white shrink-0">
      {isMobile ? (
        <MobileNav />
      ) : (
        <button onClick={toggle} className="p-1.5 rounded hover:bg-slate-100 transition-colors">
          <Icons.PanelLeftClose className="h-4 w-4 text-slate-500" />
        </button>
      )}

      {/* Mobile logo */}
      {isMobile && (
        <NavLink to="/"><img src="/assets/logo.png" alt="Pargro" className="h-7 ml-2" /></NavLink>
      )}

      <div className="flex-1" />
      <div className="flex items-center gap-2">
        <WarningBadge />
        <AvatarMenu />
      </div>
    </header>
  );
}

// ─── Layout ──────────────────────────────────────────────────────────────────

export function AppLayout() {
  return (
    <div className="flex h-dvh overflow-hidden">
      <Sidebar />
      <div className="flex flex-1 flex-col min-w-0 overflow-hidden">
        <Header />
        <main className="flex-1 overflow-y-auto bg-slate-50">
          <div className="px-3 pt-2 pb-1">
            <Breadcrumbs />
          </div>
          <div className="px-3 pb-3">
            <div className="rounded-lg border bg-white shadow-sm p-3 md:p-4 animate-fade-in">
              <LocationGuard>
                <RumPageTracker />
                <Outlet />
              </LocationGuard>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
